import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useReportStore } from '../../store/providers/Report';
import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './ManageReportsListSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const ManageReportsModal = React.lazy(() => import('../../components/Modals/ManageReportsModal/ManageReportsModal'));

function ManageReports() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, listRecords, newRecord, deleteRecord, data, clearData } = useReportStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);
    const pagePermission = constants.PERMISSION_REPORTS_MANAGEREPORTS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (report) => {
        setIsEditing(true);
        selectRecord({ reportId: report.id });
    };

    const addedHandler = () => {
        setIsEditing(true);
        newRecord();
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const deletedHandler = (report) => {
        deleteRecord({ reportId: report.id });
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        listRecords();
    };

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }
        }

        setIsBusy(!_.isEmpty(data) && (data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list')));
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'manageReports', parent: 'Reports', text: 'Manage Reports', path: window.location.pathname }));
        listRecords();
        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    return (
        <>
            <SideMenu activeKey="reports" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            isEditing && <ManageReportsModal
                                show={isEditing}
                                size="large"
                                title={(!_.isEmpty(data) && !_.isEmpty(data.body)) ? 'Edit Report' : 'Add Report'}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Manage Reports" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Report"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        added={() => addedHandler()}
                                        permissionEdit={canEdit}
                                        selected={(franchiseNews) => selectedHandler(franchiseNews)}
                                        deleted={(franchiseNews) => deletedHandler(franchiseNews)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(ManageReports));
